import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

interface Market {
  market_id:  string;
  title:      string;
  status:     string;
  total_pool: number;
}

interface Props {
  apiBase:        string;
  activeMarketId: string;
  currentUser:    string;
  onSelectMarket: (id: string, title: string) => void;
}

const BG           = '#020617';
const SCROLL_STEP  = 220; // px per chevron click

export default function MarketSelector({
  apiBase,
  activeMarketId,
  onSelectMarket,
}: Omit<Props, 'currentUser'> & { currentUser?: string }): React.JSX.Element {
  // v1.8.1 Mint Isolation — the inline "+ NEW MARKET" creator panel
  // was deprecated. The Mint (Hazard Stripe button → MintModal) is
  // the only mint surface now, so `creating / question / liquidity /
  // formErr / busy` state and `handleCreate` were removed. The ribbon
  // still displays live markets and reacts to socket `market_created`
  // events from other clients.
  const [markets,   setMarkets]   = useState<Market[]>([]);
  const [error,     setError]     = useState<string | null>(null);
  const [fadeLeft,  setFadeLeft]  = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const cancelled = useRef(false);
  const ribbonRef = useRef<HTMLDivElement>(null);

  // ── Fade state ────────────────────────────────────────────────────────────
  const updateFades = useCallback(() => {
    const el = ribbonRef.current;
    if (!el) return;
    setFadeLeft(el.scrollLeft > 4);
    setFadeRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(updateFades);
    return () => cancelAnimationFrame(id);
  }, [markets, updateFades]);

  useEffect(() => {
    window.addEventListener('resize', updateFades, { passive: true });
    return () => window.removeEventListener('resize', updateFades);
  }, [updateFades]);

  // ── Wheel → horizontal scroll ─────────────────────────────────────────────
  useEffect(() => {
    const el = ribbonRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      e.stopPropagation();
      if (el) el.scrollLeft += e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY;
    }
    el.addEventListener('wheel',  onWheel,    { passive: false });
    el.addEventListener('scroll', updateFades, { passive: true });
    updateFades();
    return () => {
      el.removeEventListener('wheel',  onWheel);
      el.removeEventListener('scroll', updateFades);
    };
  }, [updateFades]);

  // ── Fetch markets (no-cache + re-fetch on tab focus) ──────────────────────
  const fetchMarkets = useCallback(() => {
    fetch(`${apiBase}/api/markets?t=${Date.now()}`, { cache: 'no-store' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<Market[]>;
      })
      .then(data => {
        if (cancelled.current) return;
        const open = data.filter(m => m.status === 'OPEN');
        setMarkets(open);
        setError(null);
        if (!activeMarketId && open.length > 0) {
          onSelectMarket(open[0].market_id, open[0].title);
        }
      })
      .catch(e => { if (!cancelled.current) setError((e as Error).message); });
  }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    cancelled.current = false;
    fetchMarkets();

    // Re-fetch whenever the user switches back to this tab / window
    function onVisible() {
      if (document.visibilityState === 'visible') fetchMarkets();
    }
    document.addEventListener('visibilitychange', onVisible);

    const socket = io(apiBase);
    socket.on('market_created', (m: Market) => {
      if (!cancelled.current) addMarket(m);
    });

    return () => {
      cancelled.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      socket.disconnect();
    };
  }, [fetchMarkets, apiBase]);

  function addMarket(m: Partial<Market> & { market_id: string; title: string }) {
    setMarkets(prev => {
      if (prev.some(x => x.market_id === m.market_id)) return prev;
      return [...prev, { market_id: m.market_id, title: m.title, status: m.status ?? 'OPEN', total_pool: m.total_pool ?? 0 }];
    });
  }

  // ── Click-to-centre ───────────────────────────────────────────────────────
  function scrollToCenter(btn: HTMLElement) {
    const el = ribbonRef.current;
    if (!el) return;
    const mid    = el.getBoundingClientRect().left + el.clientWidth / 2;
    const btnMid = btn.getBoundingClientRect().left + btn.offsetWidth / 2;
    el.scrollBy({ left: btnMid - mid, behavior: 'smooth' });
  }

  function scrollBy(delta: number) {
    ribbonRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }

  if (error) {
    return (
      <div style={{ fontFamily: 'monospace', color: 'rgba(239,68,68,0.6)', fontSize: 10, padding: '6px 0' }}>
        Market list unavailable: {error}
      </div>
    );
  }

  // ── Chevron button ────────────────────────────────────────────────────────
  function Chevron({ dir }: { dir: 'left' | 'right' }) {
    const visible = dir === 'left' ? fadeLeft : fadeRight;
    return (
      <button
        onClick={() => scrollBy(dir === 'left' ? -SCROLL_STEP : SCROLL_STEP)}
        style={{
          flexShrink:      0,
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          width:           24,
          height:          30,
          background:      'none',
          border:          'none',
          cursor:          visible ? 'pointer' : 'default',
          color:           visible ? '#4b6080' : 'transparent',
          fontSize:        14,
          transition:      'color 0.2s',
          padding:         0,
          userSelect:      'none',
        }}
        tabIndex={-1}
        aria-hidden="true"
      >
        {dir === 'left' ? '‹' : '›'}
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0 }}>
      <style>{`.mrk-ribbon::-webkit-scrollbar{display:none}`}</style>

      {/* ── Main row: [‹] [ribbon] [›] ─────────────────────────────────────
            v1.8.1 Mint Isolation removed the legacy "+ NEW MARKET" red
            button — market creation now lives exclusively in the Mint
            modal (Hazard Stripe button at the top of the viewport). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>

        {/* Left chevron */}
        <Chevron dir="left" />

        {/* Ribbon with edge fades */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden' }}>

          {/* Left fade */}
          <div style={{
            position: 'absolute', top: 0, left: 0, bottom: 4, width: 40, zIndex: 2,
            background: `linear-gradient(to right, ${BG} 20%, transparent)`,
            opacity: fadeLeft ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: 'none',
          }} />

          {/* Right fade */}
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 4, width: 40, zIndex: 2,
            background: `linear-gradient(to left, ${BG} 20%, transparent)`,
            opacity: fadeRight ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: 'none',
          }} />

          {/* Scroll container */}
          <div
            ref={ribbonRef}
            className="mrk-ribbon"
            style={{
              display:         'flex',
              flexDirection:   'row',
              flexWrap:        'nowrap',
              alignItems:      'center',
              gap:             8,
              paddingBottom:   4,
              overflowX:       'scroll',
              overflowY:       'hidden',
              scrollbarWidth:  'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              width:           '100%',
              minWidth:        0,
              boxSizing:       'border-box',
            } as React.CSSProperties}
          >
            {markets.length === 0 && (
              <span style={{ flexShrink: 0, fontFamily: 'monospace', color: '#4b6080', fontSize: 10 }}>
                Loading markets…
              </span>
            )}

            {markets.map(m => {
              const active = m.market_id === activeMarketId;
              return (
                <button
                  key={m.market_id}
                  onClick={e => {
                    onSelectMarket(m.market_id, m.title);
                    scrollToCenter(e.currentTarget as HTMLButtonElement);
                  }}
                  title={`Pool: $${(m.total_pool ?? 0).toFixed(0)}`}
                  style={{
                    flexShrink:    0,
                    fontFamily:    'monospace',
                    fontSize:      11,
                    letterSpacing: '0.04em',
                    padding:       '6px 14px',
                    borderRadius:  4,
                    border:        `1px solid ${active ? '#2a7ab8' : '#2a3a4a'}`,
                    background:    active ? '#0d3a5a' : '#111820',
                    color:         active ? '#93c5fd' : '#8aa8c0',
                    cursor:        'pointer',
                    transition:    'border-color 0.12s, background 0.12s, color 0.12s',
                    whiteSpace:    'nowrap',
                  }}
                >
                  {m.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right chevron */}
        <Chevron dir="right" />
      </div>
    </div>
  );
}
